import React, { useCallback, useState } from 'react';

import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Card, Col, Flex, Row, type SelectProps, Space, Spin } from 'antd-v5';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Heading, Select, Text } from 'scouting-ui/lib';

import { ESBBaseError } from '@appTypes/esb/errors';
import type { RosterScout } from '@modules/advancement/duck/types';
import {
  useBulkRemoveFromEventsMutation,
  useGetCalendarEventsQuery,
} from '@modules/events/api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import toastService from '@modules/toasts/service';
import { formatToAPIDate } from '@modules/utils/datesLuxon';
import { personTagType } from '@shared';
import { PersonAvatar } from '@shared/components';
import PersonTag from '@shared/components/PersonTag';
import { navigateToRoster } from '@shared/duck/actions';
import {
  packRosterItemsSel,
  selectedOrganizationSel,
} from '@shared/duck/selectors/general.selectors';

import styles from './BulkRemovePage.scss';
import EventsTable, { type EventRow } from './components/EventsTable';

interface SelectItem {
  label: React.ReactNode;
  groupLabel: React.ReactNode;
  record: RosterScout;
  value: number;
}

type LabelRender = SelectProps['labelRender'];

function BulkRemovePageContainer() {
  const items = useSelector(packRosterItemsSel);
  const organization = useSelector(selectedOrganizationSel);
  const dispatch = useDispatch();
  const unitId = organization?.unitId;
  const [selectedPerson, setSelectedPerson] = useState<RosterScout>();
  const [bulkDelete, { isLoading: isDeleting }] =
    useBulkRemoveFromEventsMutation();
  const [selectedEvents, setSelectedEvents] = useState<{
    keys: React.Key[];
    events: EventRow[];
  }>({ keys: [], events: [] });
  const {
    data: events,
    isLoading,
    isFetching,
  } = useGetCalendarEventsQuery(
    {
      payload: {
        invitedUserId: selectedPerson?.userId,
        fromDate: formatToAPIDate(DateTime.now().startOf('day')),
        unitId: +unitId,
        toDate: formatToAPIDate(DateTime.now().endOf('day').plus({ years: 5 })),
      },
    },
    {
      skip: !selectedPerson || !selectedPerson?.userId || !unitId,
    },
  );

  const options = _.chain(items)
    .sortBy(['lastName'])
    .groupBy(item => {
      if (item.isAdult) return 'adult';
      if (item.isParent) return 'parent';
      return 'youth';
    })
    .map((items, key) => ({
      label: <Text font="body-overline">{_.startCase(key)}</Text>,
      title: key,
      options: items.map(item => {
        const group = item.isAdult
          ? 'Adult'
          : item.isParent
          ? 'Parent'
          : 'Youth';

        const option: SelectItem = {
          label: item.personFullName,
          value: item.userId,
          groupLabel: group,
          record: item,
        };

        return option;
      }),
    }))
    .value();

  const PersonSelectItem: React.FC<{ person: RosterScout }> = props => {
    const record = props.person;

    return (
      <Flex gap="small" align="center">
        <PersonAvatar
          pictureUrl={record.pictureUrl}
          isAdult={record.isAdult}
          gender={record.gender}
        />
        <Text>{record.personFullName}</Text>
      </Flex>
    );
  };

  const labelRender: LabelRender = useCallback(
    params => {
      const { label } = params;
      const record = selectedPerson;

      if (label && record) {
        const isYouth = !!(
          !record.isLeader &&
          !record.isAdult &&
          !record.isParent
        );
        const isAdult = !!(record.isAdult || record.isLeader);
        const isParent = !!record.isParent;
        return (
          <Flex gap={'small'} align="center">
            <PersonAvatar
              pictureUrl={record.pictureUrl}
              className={styles.selectedPersonAvatar}
              isAdult={record.isAdult}
              gender={record.gender}
            />
            <Flex vertical gap={0}>
              <Text>{record.personFullName}</Text>
              <Space>
                {isYouth && (
                  <PersonTag size="5" personType={personTagType.YOUTH} />
                )}
                {isParent && (
                  <PersonTag size="5" personType={personTagType.PARENT} />
                )}
                {isAdult && (
                  <PersonTag size="5" personType={personTagType.LEADER} />
                )}
              </Space>
            </Flex>
          </Flex>
        );
      }
      return undefined;
    },
    [selectedPerson],
  );

  const handleBack = () => dispatch(navigateToRoster());

  return (
    <Flex vertical className={styles.pageContainer}>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Button
            className={styles.backButton}
            type="link"
            onClick={handleBack}
            icon={<MdKeyboardArrowLeft />}
          >
            <FormattedMessage id="bulkDeleteInviteePage.backBtn" />
          </Button>
        </Col>
        <Col xs={24}>
          <Heading font="display-h4" level={1}>
            <FormattedMessage id="bulkDeleteInviteePage.title" />
          </Heading>
        </Col>
        <Col xs={24}>
          <Card>
            <Space direction="vertical">
              <Heading level={5} font="display-h5">
                <FormattedMessage id="bulkDeleteInviteePage.memberInfo" />
              </Heading>

              <Select
                placeholder={
                  <FormattedMessage id="bulkDeleteInviteePage.form.selectPerson" />
                }
                filterOption
                optionFilterProp="label"
                className={styles.personSelect}
                popupClassName={styles.personDropdown}
                listHeight={500}
                virtual={false}
                labelRender={labelRender}
                showSearch
                loading={isFetching || isLoading}
                onChange={(_, option) => {
                  const opt = option as unknown as SelectItem;
                  const record = opt.record as RosterScout;
                  setSelectedPerson(record);
                }}
                optionRender={option => {
                  const record = (option.data as unknown as SelectItem).record;

                  return <PersonSelectItem person={record} />;
                }}
                options={options}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24}>
          <Card>
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <Spin spinning={isFetching || isDeleting}>
                  <Flex vertical gap={'middle'}>
                    <Flex justify="space-between" align="center">
                      <Heading>
                        <FormattedMessage id="bulkDeleteInviteePage.events" />
                      </Heading>
                      <Button
                        danger
                        disabled={selectedEvents.keys.length === 0}
                        onClick={async () => {
                          const eventIds = selectedEvents.events.map(
                            event => event.id,
                          );
                          const userId = selectedPerson?.userId as number;

                          try {
                            await bulkDelete({
                              userId,
                              invalidate: [
                                tagFactory.UnitEvents.unitEvents(+unitId),
                              ],
                              payload: {
                                eventIds,
                              },
                            }).unwrap();
                            toastService.success(
                              <FormattedMessage id="bulkDeleteInviteePage.toast.success" />,
                            );
                          } catch (err) {
                            const error: FetchBaseQueryError = err;

                            if (error.status === 403) return;

                            const esbError = err.data as ESBBaseError;

                            toastService.error(esbError?.message);
                          }
                        }}
                      >
                        <FormattedMessage
                          id="bulkDeleteInviteePage.deleteBtn"
                          values={{ count: selectedEvents.keys.length }}
                        />
                      </Button>
                    </Flex>
                    <EventsTable
                      events={events || []}
                      onChange={(selectedKeys, selectedRows) => {
                        setSelectedEvents({
                          keys: selectedKeys,
                          events: selectedRows,
                        });
                      }}
                    />
                  </Flex>
                </Spin>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Flex>
  );
}

export default BulkRemovePageContainer;
